/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Mdx, Link } from '@ta-interaktiv/contentbricks'
import { Image } from 'components/image'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { ScrollTracking } from 'components/scrollTracking'
import { StandaloneIFrame } from 'components/standaloneIFrame'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'

const ARTICLE_ID = '15650041'
const ADDITIONAL_SHARES = 332 + 1060 + 849
const isMobile = browserCheck.isMobile

const steps = [
  {
    id: 1,
    text: (
      <>
        Schon in der Kindheit muss richtige Handhygiene erlernt werden, nur dann
        wird sie automatisiert. Als erstes die Hände unter fliessendem Wasser
        nass machen. Grober Dreck wird so schon entfernt.
      </>
    )
  },
  {
    id: 2,
    text: (
      <>
        Die Hände einseifen, wenn möglich mit Flüssigseife und nicht mit
        Seifenstücken – das ist hygienischer. Antibakterielle Wirkstoffe in der
        Seife braucht es nicht. Wer sich aber die Hände ohne Seife wäscht, kann
        es genauso gut bleiben lassen. Das sagt zumindest eine{' '}
        <Link
          href='http://www.merkur.de/bayern/uni-test-haendewaschen-ohne-seife-bringt-nichts-971254.html'
          allowWordWrap
        >
          Studie der Uni Regensburg
        </Link>
        .
      </>
    )
  },
  {
    id: 3,
    text: (
      <>
        Die Hände reiben, bis es schäumt. Dabei nicht vergessen, den Handrücken,
        zwischen den Fingern, unter den Fingernägeln und die Handgelenke zu
        reiben. Vor allem auf den Fingerkuppen, unter den Nägeln, zwischen den
        Fingern und auch am Daumen befinden sich viele Bakterien, die
        weggewaschen werden müssen.
      </>
    )
  },
  {
    id: 4,
    text: (
      <>
        Für effektives Händewaschen braucht man mindestens 20 Sekunden, besser
        30 Sekunden. Im Schnitt wenden die Menschen aber nur sechs Sekunden
        dafür auf. Eine halbe Minute einseifen tötet 99,9 Prozent der Erreger
        auf den Händen ab.
      </>
    )
  },
  {
    id: 5,
    text: (
      <>
        Die Hände unter fliessendem Wasser gut abspülen. Die Wassertemperatur
        ist dabei vollkommen egal, sie beeinflusst die Keimzahl nicht. Wichtig
        ist auch, dass die Hände gepflegt sind. Lädierte Haut kann ein richtiges
        Mikrobennest sein. Zudem sollten die Fingernägel kurz geschnitten sein.
      </>
    )
  },
  {
    id: 6,
    text: (
      <>
        Die Hände mit einem sauberen Handtuch trocknen, wenn möglich mit einem
        Wegwerf-Papiertuch, damit lassen sich auf den Fingerkuppen verbleibende
        Keime nochmals durch Abreiben reduzieren. Beim Trocknen mit
        Heissluftgeräten und Textilhandtüchern ist hingegen eine Zunahme der
        Keimbelastung möglich. Achtung: Feuchte Hände übertragen bis zu 1000-mal
        mehr Keime als trockene Hände. Wenn Sie sich in öffentlichen WCs die
        Hände waschen, drehen Sie den Wasserhahn am besten mit einem Papiertuch
        ab, und öffnen Sie auch die Türe mit dem Ellbogen.
      </>
    )
  }
]
export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} additionalShares={ADDITIONAL_SHARES} />
      <ScrollTracking />
      <ThemeSwitch />

      <header className='ui vertical fitted segment'>
        <div className='ui container'>
          <h1 className='ui image header'>
            <div className='content'>
              Es hilft gegen Viren – und alle machen es falsch
              <div className='sub header lead'>
                Händewaschen hilft zuverlässig gegen Viren. Wir zeigen, warum es
                über 90 Prozent falsch machen.
              </div>
            </div>
          </h1>
        </div>
      </header>

      <div className='ui vertical very fitted  segment'>
        <div className='ui text container '>
          <div className='metainfo'>
            <div className='author'>
              <nobr>Marc Brupbacher</nobr> und{' '}
              <nobr>Sebastian Broschinski</nobr>
            </div>
            <div className='updated'>Aktualisiert am 2. April 2020 </div>
          </div>
          <Mdx file={require('text/intro.mdx')} />

          <Image
            className='cbImage noBottomSpacing'
            file={require('images/notwashed-01.jpg')}
            title={<>Bereiche der Hände werden beim Waschen oft vergessen</>}
            sub={
              <>
                Diese schon etwas in die Jahre gekommene Visualisierung ist
                wieder brandaktuell. Sie zeigt, wie gut das
                US-Gesundheitspersonal sich die Hände wäscht und welche Bereiche
                oft vergessen gehen. Viele Menschen waschen nur die
                Handinnenflächen, Sie sollten aber alle Bereiche der Hand
                gründlich abreiben.
              </>
            }
            source={
              <>
                Quelle:Taylor, LJ.{' '}
                <Link
                  href='https://www.wakemed.org/documents/ForEmployees/Hand-Hygiene-Frequently-Missed-Areas.pdf'
                  allowWordWrap
                >
                  An evaluation of handwashingtechniques.
                </Link>{' '}
                NursingTimes. Januar 1978.
              </>
            }
          >
            <div className='handAreasKey'>
              <div className='entry'>
                <div className='color red' />
                <div className='label'>am meisten vergessen</div>
              </div>
              <div className='entry'>
                <div className='color orange' />
                <div className='label'>oft vergessen</div>
              </div>
              <div className='entry'>
                <div className='color yellow' />
                <div className='label'>gelegentlich vergessen</div>
              </div>
            </div>
          </Image>
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container '>
          <Mdx file={require('text/problem.mdx')} />
        </div>
      </div>
      <div className='ui vertical segment'>
        <div className='ui text container '>
          <h3>Richtig Händewaschen in sechs Schritten.</h3>
        </div>
      </div>
      <div className='ui vertical segment'>
        <div className='ui  container '>
          <div className='stepContainer'>
            <div className='ui centered grid'>
              {steps.map((step, i) => {
                return (
                  <div
                    className={(isMobile ? 'sixteen' : 'five') + ' wide column'}
                    key={i}
                  >
                    <div className='number'>{step.id + '.'}</div>
                    <img
                      src={require('images/wasch_illu-0' + step.id + '.jpg')}
                      width='100%'
                    />
                    <div className='text'>{step.text}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* <div className='ui vertical segment'>
        <div className='ui text container '>
          <Mdx file={require('text/tips.mdx')} />
        </div>
      </div> */}
      <div className='ui vertical segment'>
        <div className='ui text container '>
          <Mdx file={require('text/when.mdx')} />
        </div>
        <div className='ui text container'>
          {/* <SchutzMassnahmen /> */}
          <StandaloneIFrame
            url='https://interaktiv.tagesanzeiger.ch/2020/corona-schutz-massnahmen/'
            id='SchutzMassnahmen'
          />

          {/* <AutofitIFrame
            id='testFrame'
            title='So können Sie sich schützen'
            aria-label='Bullet Balken'
            src='https://interaktiv.tagesanzeiger.ch/2020/corona-schutz-massnahmen/'
          /> */}
        </div>
      </div>
      {/* <div className='ui vertical segment'>
        <div className='ui text container '>
          <Mdx file={require('text/pandemie.mdx')} />
        </div>
      </div> */}

      <Footer
        additionalShares={ADDITIONAL_SHARES}
        articleId={ARTICLE_ID}
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie gefällt Ihnen die Gestaltung?',
          'Wie gut haben Sie das letzte Mal die Hände gewaschen?'
        ]}
        credits={[
          ['Konzept und Umsetzung', 'Marc Brupbacher, Sebastian Broschinski'],
          [
            'Quellen',
            'Bundesamt für Gesundheit (BAG), Robert-Koch-Institut, Weltgesundheitsorganisation WHO, www.sciencemediacenter.de'
          ]
        ]}
      >
        <div
          className='ui container'
          style={{ margin: isMobile ? '1.2em 0 3em' : '0 0 3em' }}
        >
          <h4 className='teaser'>Weitere Beiträge zum Thema</h4>
          <ArticleTeasers
            numberOfCards='three'
            articleIds={[
              '11435150',
              '19483314',
              '13878467',
              '378065708166',
              '31092907',
              '10448437'
            ]}
          />
        </div>
      </Footer>
    </div>
  )
}
